import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="about" className="section-padding-mobile bg-gray-50 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-12 w-48 h-48 xs:-left-24 xs:w-96 xs:h-96 bg-primary-100/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-12 w-48 h-48 xs:-right-24 xs:w-96 xs:h-96 bg-accent-100/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container-mobile relative">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 xs:mb-16 lg:mb-20"
        >

          <h2 id="about-heading" className="text-mobile-title font-bold text-gray-900 mb-4 xs:mb-6">
            <span className="gradient-text">About Us</span>
          </h2>
          <p className="text-mobile-body text-gray-600 max-w-4xl mx-auto">
            Pioneering the future of infrastructure monitoring through innovation and excellence
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 xs:gap-12 lg:gap-16 items-start">
          {/* Our Vision */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-4 xs:space-y-6"
          >
            <div className="flex items-center space-x-3 xs:space-x-4 mb-4 xs:mb-6">
              <div className="w-10 h-10 xs:w-12 xs:h-12 bg-primary-100 rounded-lg xs:rounded-xl flex items-center justify-center shadow-mobile-soft xs:shadow-soft">
                <svg className="w-5 h-5 xs:w-6 xs:h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3 className="text-xl xs:text-2xl lg:text-3xl font-bold text-gray-900">
                Our Vision
              </h3>
            </div>
            
            <div className="bg-white rounded-xl xs:rounded-2xl p-6 xs:p-8 shadow-mobile-large xs:shadow-lg border border-gray-100">
              <p className="text-base xs:text-lg text-gray-600 leading-relaxed">
                At CIVIONIKS, we envision a world where engineers and decision-makers are equipped with advanced tools 
                to transform how structural integrity is assessed and maintained. Our mission is to empower professionals 
                globally by redefining the approach to structural analysis and monitoring, ensuring safety, efficiency, 
                and sustainability for every project.
              </p>
              <p className="text-base xs:text-lg text-gray-600 leading-relaxed mt-3 xs:mt-4">
                By leveraging AI, machine learning, and state-of-the-art visualization, we are building a future where 
                data-driven decisions safeguard the structures that shape our world.
              </p>
            </div>

            {/* Vision highlights */}
            <div className="grid grid-cols-2 gap-3 xs:gap-4">
              <div className="bg-white p-3 xs:p-4 rounded-lg xs:rounded-xl shadow-mobile-soft xs:shadow border border-gray-100">
                <div className="text-xl xs:text-2xl font-bold text-primary-600 mb-1">Global</div>
                <div className="text-xs xs:text-sm text-gray-600">Reach & Impact</div>
              </div>
              <div className="bg-white p-3 xs:p-4 rounded-lg xs:rounded-xl shadow-mobile-soft xs:shadow border border-gray-100">
                <div className="text-xl xs:text-2xl font-bold text-primary-600 mb-1">Safety</div>
                <div className="text-xs xs:text-sm text-gray-600">First Approach</div>
              </div>
            </div>
          </motion.div>

          {/* Our Technology */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-4 xs:space-y-6"
          >
            <div className="flex items-center space-x-3 xs:space-x-4 mb-4 xs:mb-6">
              <div className="w-10 h-10 xs:w-12 xs:h-12 bg-primary-100 rounded-lg xs:rounded-xl flex items-center justify-center shadow-mobile-soft xs:shadow-soft">
                <svg className="w-5 h-5 xs:w-6 xs:h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl xs:text-2xl lg:text-3xl font-bold text-gray-900">
                Our Technology
              </h3>
            </div>
            
            <div className="bg-white rounded-xl xs:rounded-2xl p-6 xs:p-8 shadow-mobile-large xs:shadow-lg border border-gray-100">
              <p className="text-base xs:text-lg text-gray-600 leading-relaxed">
                At the core of CIVIONIKS lies cutting-edge technology that integrates seamlessly with the engineering workflow. 
                Our software incorporates advanced AI and ML algorithms to analyze real-time data from sensors, delivering 
                unparalleled accuracy in detecting and predicting structural risks.
              </p>
              <p className="text-base xs:text-lg text-gray-600 leading-relaxed mt-3 xs:mt-4">
                With powerful visualization tools and user-friendly interfaces, CIVIONIKS transforms complex data into 
                actionable insights. From detecting cracks to measuring tilt and strain, our solutions ensure efficiency, 
                precision, and informed decision-making — driving the future of engineering analysis.
              </p>
            </div>

            {/* Technology features */}
            <div className="space-y-2 xs:space-y-3">
              <div className="flex items-center space-x-2 xs:space-x-3 bg-white p-3 xs:p-4 rounded-lg xs:rounded-xl shadow-mobile-soft xs:shadow border border-gray-100">
                <div className="w-1.5 h-1.5 xs:w-2 xs:h-2 bg-primary-500 rounded-full"></div>
                <span className="text-gray-700 font-medium text-sm xs:text-base">Advanced AI & ML Algorithms</span>
              </div>
              <div className="flex items-center space-x-2 xs:space-x-3 bg-white p-3 xs:p-4 rounded-lg xs:rounded-xl shadow-mobile-soft xs:shadow border border-gray-100">
                <div className="w-1.5 h-1.5 xs:w-2 xs:h-2 bg-primary-500 rounded-full"></div>
                <span className="text-gray-700 font-medium text-sm xs:text-base">Real-time Data Processing</span>
              </div>
              <div className="flex items-center space-x-2 xs:space-x-3 bg-white p-3 xs:p-4 rounded-lg xs:rounded-xl shadow-mobile-soft xs:shadow border border-gray-100">
                <div className="w-1.5 h-1.5 xs:w-2 xs:h-2 bg-primary-500 rounded-full"></div>
                <span className="text-gray-700 font-medium text-sm xs:text-base">Powerful Visualization Tools</span>
              </div>
              <div className="flex items-center space-x-2 xs:space-x-3 bg-white p-3 xs:p-4 rounded-lg xs:rounded-xl shadow-mobile-soft xs:shadow border border-gray-100">
                <div className="w-1.5 h-1.5 xs:w-2 xs:h-2 bg-primary-500 rounded-full"></div>
                <span className="text-gray-700 font-medium text-sm xs:text-base">User-friendly Interfaces</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Company stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-12 xs:mt-16 bg-white rounded-xl xs:rounded-2xl shadow-mobile-large xs:shadow-lg p-6 xs:p-8 border border-gray-100"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 xs:gap-6 lg:gap-8 text-center">
            <div>
              <div className="text-2xl xs:text-3xl font-bold text-primary-600 mb-1 xs:mb-2">67+</div>
              <div className="text-gray-600 text-xs xs:text-sm">Supported Sensors</div>
            </div>
            <div>
              <div className="text-2xl xs:text-3xl font-bold text-primary-600 mb-1 xs:mb-2">99.9%</div>
              <div className="text-gray-600 text-xs xs:text-sm">Uptime Reliability</div>
            </div>
            <div>
              <div className="text-2xl xs:text-3xl font-bold text-primary-600 mb-1 xs:mb-2">24/7</div>
              <div className="text-gray-600 text-xs xs:text-sm">Real-time Monitoring</div>
            </div>
            <div>
              <div className="text-2xl xs:text-3xl font-bold text-primary-600 mb-1 xs:mb-2">Global</div>
              <div className="text-gray-600 text-xs xs:text-sm">Infrastructure Focus</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Section divider */}
      <div className="section-divider mt-16 xs:mt-20 lg:mt-24"></div>
    </section>
  );
};

export default About; 